import { apiClient } from '../client'
import { FieldType } from '../../app/modules/types/workflow'

export type ComponentConfigStatus = 'configured' | 'not_configured' | 'needs_attention'

/** A row of `GET /workflow/component-configs`. */
export interface ComponentConfigSummary {
  component_id: string
  component_name: string
  display_name: string
  icon: string | null
  status: ComponentConfigStatus
  /** Null when the component has never been configured. */
  updated_at: string | null
  updated_by: string | null
}

/** `GET /workflow/component-configs/:componentId` — also the body of a 200 PUT. */
export interface ComponentConfigDetail {
  component_id: string
  component_name: string
  display_name: string
  /** The node-config modal's frontend-schema shape; secret fields carry `secret: true`. */
  schema: {
    routes: { label: string; path: string }[]
    map: Record<string, FieldType[]>
  }
  /** Non-secret fields only, declared defaults already applied. */
  values: Record<string, unknown>
  /** Secrets are write-only: only whether one is stored comes back. */
  secrets: Record<string, { is_set: boolean }>
  /** Field names whose stored value no longer matches the component's settings. */
  invalid_fields: string[]
  status: ComponentConfigStatus
  updated_at: string | null
  updated_by: string | null
}

/**
 * PUT body. For a secret: omit the key (or send empty) to keep the stored value,
 * a string to replace it, `null` to clear it.
 */
export interface SaveComponentConfigPayload {
  config: Record<string, unknown>
}

/**
 * Centralized component config — per-organization values a component declares
 * once instead of in every workflow. Org scope rides on the
 * `x-current-organization` header the client adds.
 */
export const componentConfigsApi = {
  list: async (): Promise<ComponentConfigSummary[]> => {
    const { data } = await apiClient.get('/workflow/component-configs')
    return data?.data ?? []
  },

  getById: async (componentId: string): Promise<ComponentConfigDetail> => {
    const { data } = await apiClient.get(`/workflow/component-configs/${componentId}`)
    return data
  },

  /** 200 answers with the detail body — callers replace local state with it. */
  save: async (
    componentId: string,
    payload: SaveComponentConfigPayload
  ): Promise<ComponentConfigDetail> => {
    const { data } = await apiClient.put(`/workflow/component-configs/${componentId}`, payload)
    return data
  },

  /** Deletes this organization's values, secrets included. 204, no body. */
  reset: async (componentId: string): Promise<void> => {
    await apiClient.delete(`/workflow/component-configs/${componentId}`)
  },
}
